import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { apiUrl } from '../utils/apiBase';
import { trackPlatformActivity } from '../api.js';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    // Statt einen Error zu werfen, geben wir einen Default-Wert zurück
        return {
      user: null,
      isAuthenticated: false,
      isLoading: true,
      error: null,
      source: null,
      setSource: () => {},
      login: async () => ({ success: false, error: 'Auth not available' }),
      devBypass: async () => ({ success: false, error: 'Auth not available' }),
      logout: async () => ({ success: false, error: 'Auth not available' }),
      checkPlanAccess: () => false,
      checkAuthStatus: async () => {}
    };
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  // 🆕 WICHTIG: source Parameter für Wallet-Typ
  const [source, setSource] = useState(null);
  // 🆕 NEU: Zuletzt gekaufter Plan
  const [latestPlan, setLatestPlan] = useState(null);
  // 🆕 NEU: Ref um zu tracken ob latestPlan bereits geladen wurde (verhindert circular dependency)
  const latestPlanLoadedRef = useRef(false);
  // 🆕 NEU: Ref um zu tracken ob wir gerade devBypass versuchen (verhindert race conditions)
  const devBypassInProgressRef = useRef(false);
  // 🚀 FIX: Refs für stabile Referenzen (verhindert Endlosschleife)
  const userRef = useRef(user);
  const isAuthenticatedRef = useRef(isAuthenticated);
  const latestPlanRef = useRef(latestPlan);
  
  // Aktualisiere Refs wenn State sich ändert
  useEffect(() => {
    userRef.current = user;
    isAuthenticatedRef.current = isAuthenticated;
    latestPlanRef.current = latestPlan;
  }, [user, isAuthenticated, latestPlan]);

  // 🔹 Macht lokalen Auth sofort im UI sichtbar (ohne Reload)
  const applyLocalAuth = (authData) => {
    if (!authData?.walletAddress) return;
    try {
      sessionStorage.removeItem("v3tax:force_logged_out");
    } catch {}
    setUser({
      walletAddress: authData.walletAddress,
      userId: authData.userId || null, // 🚀 NEU: user_id speichern
      plan: authData.plan || 'free',
      isDev: !!authData.isDev,
    });
    setIsAuthenticated(true);
    setIsLoading(false);
      };

  // 🚀 WICHTIG: devBypass MUSS VOR checkAuthStatus und loadLatestPlan definiert werden
  const devBypass = useCallback(async (walletAddress, options = {}) => {
    const { skipLocalAuth = false, source = null } = options;

    try {
      setError(null);
      const response = await fetch(apiUrl('/api/auth/dev-bypass'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({ walletAddress })
      });

      if (response.ok) {
        const data = await response.json();
        
        const userData = {
          walletAddress: data.walletAddress,
          userId: data.userId || null,
          plan: data.plan || 'standard',
          isDev: true
        };

        if (!skipLocalAuth) {
          setUser(userData);
          setIsAuthenticated(true);
          
          const authData = {
            walletAddress: data.walletAddress,
            userId: data.userId || null,
            plan: data.plan || 'standard',
            isDev: true,
            authenticated: true,
            loginTime: new Date().toISOString(),
            ...(source ? { source } : {}),
          };
          localStorage.setItem('v3tax_auth', JSON.stringify(authData));

          trackPlatformActivity("login", {
            source: authData.source || source || "dev_bypass_wallet_login",
            walletAddress: authData.walletAddress,
            plan: authData.plan,
            userId: authData.userId || null,
          }).catch(() => {});
                  }

        try {
          sessionStorage.removeItem("v3tax:force_logged_out");
        } catch {}

                return { success: true, data };
      }

      const errorData = await response.json();
      throw new Error(errorData.message || 'DEV bypass failed');
    } catch (error) {
      // Handle network errors specifically
      if (error.name === 'TypeError' && error.message.includes('fetch')) {
        const networkError = 'Backend server not reachable. Please check your connection and try again.';
        setError(networkError);
        return { success: false, error: networkError };
      }

      setError(error.message);
      return { success: false, error: error.message };
    }
  }, []);

  // 🆕 NEU: Lade den zuletzt gekauften Plan
  const loadLatestPlan = useCallback(async (walletAddress) => {
    if (!walletAddress) return;
    
        
    // 🚀 Versuche zuerst die neue API-Route
    try {
      const response = await fetch(apiUrl(`/api/auth/user/latest-plan`), {
        credentials: 'include'
      });
      
      if (response.ok) {
        const data = await response.json();
        if (data.success) {
                    setLatestPlan(data);
          return;
        }
      }
    } catch (error) {
          }
    
    // 🚀 Fallback: Versuche es mit der alten Route
    try {
      const response = await fetch(apiUrl(`/api/auth/user/purchases`), {
        credentials: 'include'
      });
      
      if (response.ok) {
        const data = await response.json();
        if (data.purchases && data.purchases.length > 0) {
          // Sortiere nach Kaufdatum und nehme den neuesten
          const sortedPurchases = data.purchases.sort((a, b) => 
            new Date(b.purchaseDate) - new Date(a.purchaseDate)
          );
          const latestPurchase = sortedPurchases[0];
                    setLatestPlan({
            plan: latestPurchase.plan,
            purchaseDate: latestPurchase.purchaseDate,
            year: latestPurchase.year
          });
          return;
        }
      }
    } catch (error) {
          }
    
    // 🚀 Finaler Fallback: Verwende den Plan aus dem User-Objekt
    setLatestPlan(prevPlan => {
      // Prüfe ob bereits ein Plan geladen wurde (verhindert unnötige Updates)
      if (prevPlan) return prevPlan;
      
      // Fallback: Setze auf 'free'
            return {
        plan: 'free',
        purchaseDate: null,
        year: null
      };
    });
  }, []);

  const checkAuthStatus = useCallback(async ({ silent = false } = {}) => {
    try {
      if (!silent) setIsLoading(true);

      const forceLoggedOut =
        typeof sessionStorage !== "undefined" &&
        sessionStorage.getItem("v3tax:force_logged_out") === "1";

      const localAuth = localStorage.getItem("v3tax_auth");

      const freshMobileLoginInProgress =
        typeof sessionStorage !== "undefined" &&
        sessionStorage.getItem("v3tax:mobile_fresh_login") === "1";

      if (forceLoggedOut && !localAuth) {
        
        setUser(null);
        setIsAuthenticated(false);
        setLatestPlan(null);

        if (!silent) setIsLoading(false);
        return;
      }
      
      // 🚀 WICHTIG: Erst localStorage prüfen für lokale VeWorld-Authentifizierung
      let localAuthData = null;
      
      if (localAuth && !freshMobileLoginInProgress) {
        try {
          const parsed = JSON.parse(localAuth);
          if (parsed.authenticated && parsed.walletAddress) {
            localAuthData = parsed;
                        const userData = {
              walletAddress: parsed.walletAddress,
              userId: parsed.userId || null, // 🚀 NEU: user_id speichern (aus localStorage)
              plan: parsed.plan || 'free',
              isDev: parsed.isDev || false
            };
            // 🚀 FIX: State nur ändern wenn sich wirklich etwas geändert hat
            if (userRef.current?.walletAddress?.toLowerCase() !== userData.walletAddress.toLowerCase()) {
              setUser(userData);
            }
            if (!isAuthenticatedRef.current) {
              setIsAuthenticated(true);
            }
                      }
        } catch (parseError) {
                  }
      } else if (localAuth && freshMobileLoginInProgress) {
              }

      const localAuthSource = localAuthData?.source || null;
      const isWalletConnectLocal = localAuthSource === 'wallet-connect';
      
      // 🚀 KRITISCH: IMMER Backend prüfen, auch wenn lokale Auth vorhanden ist!
      // Das stellt sicher, dass der Backend-Cookie existiert (für API-Calls)
      try {
        const response = await fetch(apiUrl('/api/auth/status'), {
          credentials: 'include'
        });
        
        if (response.ok) {
          const data = await response.json();
          if (data.authenticated) {
            const expectedWallet =
              typeof sessionStorage !== "undefined"
                ? sessionStorage.getItem("v3tax:expected_wallet_after_login")
                : null;

            if (
              expectedWallet &&
              data?.walletAddress &&
              data.walletAddress.toLowerCase() !== expectedWallet.toLowerCase()
            ) {
              if (freshMobileLoginInProgress) {
                
                if (!silent) setIsLoading(false);
                return;
              }

              
              localStorage.removeItem('v3tax_auth');
              setUser(null);
              setIsAuthenticated(false);
              setLatestPlan(null);

              if (!silent) setIsLoading(false);
              return;
            }

            if (
              expectedWallet &&
              data?.walletAddress?.toLowerCase() === expectedWallet.toLowerCase()
            ) {
              sessionStorage.removeItem("v3tax:expected_wallet_after_login");
            }

            // Backend-Cookie existiert → alles gut
            // 🚀 FIX: Helper-Funktion um "free" zu ignorieren (verhindert localStorage-Überschreibung)
            const pickPaid = (p) => {
              const s = (p ?? '').toString().trim();
              if (!s) return null;
              return s.toLowerCase() === 'free' ? null : s;
            };
            
            // 🚀 FIX: Plan niemals auf 'free' zurücksetzen, localAuthData kommt erst am Ende (verhindert localStorage-Überschreibung)
            const resolvedPlan =
              pickPaid(data.plan) ||
              pickPaid(latestPlanRef.current?.plan) ||
              pickPaid(userRef.current?.plan) ||
              pickPaid(localAuthData?.plan) ||
              'free';
            
            const userData = {
              walletAddress: data.walletAddress,
              userId: data.userId || null,
              plan: resolvedPlan,
              isDev: data.isDev || false
            };
            
            // ✅ merge, aber nur updaten wenn sich was wirklich ändert (verhindert Render-Spam)
            setUser((prev) => {
              if (!prev) return userData;
              const sameWallet = (prev.walletAddress || '').toLowerCase() === (userData.walletAddress || '').toLowerCase();
              const samePlan = (prev.plan || '') === (userData.plan || '');
              const sameUserId = (prev.userId ?? null) === (userData.userId ?? null);
              const sameDev = !!prev.isDev === !!userData.isDev;
              if (sameWallet && samePlan && sameUserId && sameDev) return prev;
              return { ...prev, ...userData };
            });
            
            // userId auch im localStorage persistieren (mit korrektem Plan)
            try {
              const ls = JSON.parse(localStorage.getItem('v3tax_auth') || '{}');
              localStorage.setItem('v3tax_auth', JSON.stringify({ 
                ...ls, 
                ...userData, 
                plan: resolvedPlan,
                authenticated: true 
              }));
            } catch {}
            
            if (!isAuthenticatedRef.current) {
              setIsAuthenticated(true);
            }

            try {
              const w = String(data.walletAddress || "").toLowerCase();
              const trackKey = `v3tax:activity_session:${w}`;
              const last = Number(sessionStorage.getItem(trackKey) || 0);
              if (w && (!last || Date.now() - last > 12 * 3600000)) {
                sessionStorage.setItem(trackKey, String(Date.now()));
                trackPlatformActivity("login", {
                  source: localAuthSource || data.source || "session_active",
                  walletAddress: data.walletAddress,
                  plan: resolvedPlan,
                  userId: data.userId || null,
                }).catch(() => {});
              }
            } catch {}
            
            // 🆕 NEU: Lade den zuletzt gekauften Plan (nur einmal beim ersten Load)
            if (!latestPlanLoadedRef.current) {
              await loadLatestPlan(data.walletAddress);
              latestPlanLoadedRef.current = true;
            }
          } else {
            // Backend-Cookie fehlt
            if (localAuthData && localAuthData.walletAddress) {
              if (isWalletConnectLocal) {
                                localStorage.removeItem('v3tax_auth');
                setUser(null);
                setIsAuthenticated(false);
                setLatestPlan(null);
              } else {
                              }
            } else {
              // Kein Cookie und keine Local Auth → wirklich ausloggen
              // 🚀 FIX: State nur ändern wenn sich wirklich etwas geändert hat
              if (userRef.current !== null) setUser(null);
              if (isAuthenticatedRef.current) setIsAuthenticated(false);
              if (latestPlanRef.current !== null) setLatestPlan(null);
            }
          }
        } else {
          // Backend-Response fehlgeschlagen
          if (localAuthData && localAuthData.walletAddress) {
            if (isWalletConnectLocal) {
                            localStorage.removeItem('v3tax_auth');
              setUser(null);
              setIsAuthenticated(false);
              setLatestPlan(null);
            } else {
                          }
          } else if (!localAuthData) {
            // 🚀 FIX: State nur ändern wenn sich wirklich etwas geändert hat
            if (userRef.current !== null) setUser(null);
            if (isAuthenticatedRef.current) setIsAuthenticated(false);
            if (latestPlanRef.current !== null) setLatestPlan(null);
          }
        }
      } catch (backendError) {
                
        // Handle network errors specifically
        if (backendError.name === 'TypeError' && backendError.message.includes('fetch')) {
                    // Bei Netzwerkfehler: Wenn lokale Auth vorhanden, behalte sie
          if (localAuthData && localAuthData.walletAddress) {
            if (isWalletConnectLocal) {
                            localStorage.removeItem('v3tax_auth');
              setUser(null);
              setIsAuthenticated(false);
              setLatestPlan(null);
            } else {
                          }
          } else {
            // 🚀 FIX: State nur ändern wenn sich wirklich etwas geändert hat
            if (userRef.current !== null) setUser(null);
            if (isAuthenticatedRef.current) setIsAuthenticated(false);
            if (latestPlanRef.current !== null) setLatestPlan(null);
          }
        } else {
          // Andere Fehler: Bei lokaler Auth behalten, sonst ausloggen
          if (localAuthData) {
            if (isWalletConnectLocal) {
                            localStorage.removeItem('v3tax_auth');
              setUser(null);
              setIsAuthenticated(false);
              setLatestPlan(null);
            } else {
                          }
          } else {
            // 🚀 FIX: State nur ändern wenn sich wirklich etwas geändert hat
            if (userRef.current !== null) setUser(null);
            if (isAuthenticatedRef.current) setIsAuthenticated(false);
            if (latestPlanRef.current !== null) setLatestPlan(null);
          }
        }
      }
    } catch (error) {
            
      // Bei Fehler: Wenn lokale Auth vorhanden, behalte sie (außer WalletConnect ohne Backend-Garantie)
      const localAuth = localStorage.getItem('v3tax_auth');
      if (localAuth) {
        try {
          const authData = JSON.parse(localAuth);
          if (authData.authenticated && authData.walletAddress) {
            if (authData.source === 'wallet-connect') {
                            localStorage.removeItem('v3tax_auth');
              if (userRef.current !== null) setUser(null);
              if (isAuthenticatedRef.current) setIsAuthenticated(false);
              if (latestPlanRef.current !== null) setLatestPlan(null);
              return;
            }
            const userData = {
              walletAddress: authData.walletAddress,
              userId: authData.userId || null, // 🚀 NEU: user_id speichern (aus localStorage)
              plan: authData.plan || 'free',
              isDev: authData.isDev || false
            };
            // 🚀 FIX: State nur ändern wenn sich wirklich etwas geändert hat
            if (userRef.current?.walletAddress?.toLowerCase() !== userData.walletAddress.toLowerCase()) {
              setUser(userData);
            }
            if (!isAuthenticatedRef.current) {
              setIsAuthenticated(true);
            }
            return;
          }
        } catch (e) {
          // Ignoriere Parse-Fehler
        }
      }
      
      // 🚀 FIX: State nur ändern wenn sich wirklich etwas geändert hat
      if (userRef.current !== null) setUser(null);
      if (isAuthenticatedRef.current) setIsAuthenticated(false);
      if (latestPlanRef.current !== null) setLatestPlan(null);
    } finally {
      if (!silent) setIsLoading(false);
    }
  }, [devBypass, loadLatestPlan]); // 🚀 FIX: user, isAuthenticated, latestPlan entfernt - verhindert Endlosschleife

  // 🚀 WICHTIG: Aktualisiere user.plan wenn latestPlan geladen wird
  useEffect(() => {
    if (latestPlan && latestPlan.plan && latestPlan.plan !== 'free' && user) {
      // Aktualisiere user.plan nur wenn latestPlan einen bezahlten Plan hat
      if (user.plan !== latestPlan.plan) {
                setUser(prev => ({
          ...prev,
          plan: latestPlan.plan
        }));
        
        // Aktualisiere auch localStorage
        try {
          const ls = JSON.parse(localStorage.getItem('v3tax_auth') || '{}');
          localStorage.setItem('v3tax_auth', JSON.stringify({ 
            ...ls, 
            plan: latestPlan.plan 
          }));
        } catch (e) {
                  }
      }
    }
  }, [latestPlan, user]);

  // Prüfe Auth-Status beim Start
  useEffect(() => {
    checkAuthStatus();
  }, [checkAuthStatus]);

  // 🔹 Cross-Tab & Tab-Visibility: Status aktuell halten
  useEffect(() => {
    const onLocal = () => {
            checkAuthStatus({ silent: true });
    };
    const onStorage = (e) => {
      if (e.key === 'v3tax_auth') {
                checkAuthStatus({ silent: true });
      }
    };
    const onVis = () => {
      if (!document.hidden) {
                checkAuthStatus({ silent: true });
      }
    };
    window.addEventListener('v3tax-auth', onLocal);
    window.addEventListener('storage', onStorage);
    document.addEventListener('visibilitychange', onVis);
    return () => {
      window.removeEventListener('v3tax-auth', onLocal);
      window.removeEventListener('storage', onStorage);
      document.removeEventListener('visibilitychange', onVis);
    };
  }, [checkAuthStatus]);

  const login = async (loginData = {}) => {
    const {
      skipLatestPlan = false,
      latestPlanMode = 'await', // "await" | "async"
      ...loginPayload
    } = loginData || {};

    try {
      setError(null);
      const response = await fetch(apiUrl('/api/auth/login'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify(loginPayload)
      });

      if (response.ok) {
        const data = await response.json();
        // 🚀 FIX: Login-Endpoint gibt data.user zurück, nicht data direkt
        const userData = data.user || data;
        const walletAddress = userData.walletAddress || data.walletAddress;
        
        const authData = {
          walletAddress: walletAddress,
          userId: userData.userId || data.userId || null, // 🚀 NEU: user_id speichern
          plan: userData.plan || data.plan || 'free', // Wird später durch latestPlan überschrieben
          isDev: userData.isDev || data.isDev || false,
          authenticated: true,
          loginTime: new Date().toISOString(),
          // Quelle des Logins (wichtig für VeWorld / WalletConnect)
          source: loginPayload?.source || loginPayload?.walletType || source || null,
        };

        setUser({
          walletAddress: authData.walletAddress,
          userId: authData.userId,
          plan: authData.plan,
          isDev: authData.isDev,
        });
        setIsAuthenticated(true);

        localStorage.setItem('v3tax_auth', JSON.stringify(authData));

        trackPlatformActivity("login", {
          source: authData.source || loginPayload?.source || loginPayload?.walletType || source || "auth_login",
          walletAddress: authData.walletAddress,
          plan: authData.plan,
          userId: authData.userId || null,
        }).catch(() => {});

        if (authData.source) {
          setSource(authData.source);
        }
        
        try {
          sessionStorage.removeItem("v3tax:force_logged_out");
        } catch {}
        
        // 🚀 WICHTIG:
        // Embedded-Mobile-Login darf NICHT auf Plan-/Purchase-Nachlade-Requests warten.
        // Sonst ist Backend-Login schon erfolgreich, aber die WebView hängt noch im
        // Login-Promise und wirkt eingefroren.
        if (walletAddress) {
          latestPlanLoadedRef.current = false;
          if (skipLatestPlan) {
            // Für Embedded VeWorld: sofort zurückkehren.
          } else if (latestPlanMode === 'async') {
            setTimeout(() => {
              loadLatestPlan(walletAddress)
                .catch(() => {})
                .finally(() => {
                  latestPlanLoadedRef.current = true;
                });
            }, 0);
          } else {
            await loadLatestPlan(walletAddress);
            latestPlanLoadedRef.current = true;
          }
        }
        
        return { success: true, data };
      } else {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Login failed');
      }
    } catch (error) {
      setError(error.message);
      return { success: false, error: error.message };
    }
  };

  // 🚨 NEW: MetaMask login function
  const loginWithMetaMask = async (walletAddress) => {
    try {
      setError(null);
      
      // Use the same devBypass endpoint for MetaMask (since it's also a wallet connection)
      const response = await fetch(apiUrl('/api/auth/dev-bypass'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({ walletAddress })
      });

      if (response.ok) {
        const data = await response.json();
        setUser({
          walletAddress: data.walletAddress,
          userId: data.userId || null, // 🚀 NEU: user_id speichern
          plan: data.plan || 'standard',
          isDev: false // MetaMask users are not dev users
        });
        setIsAuthenticated(true);
        
        // 🚀 WICHTIG: Lokale Authentifizierung für VeWorld-Integration setzen
        const authData = {
          walletAddress: data.walletAddress,
          userId: data.userId || null, // 🚀 NEU: user_id speichern
          plan: data.plan || 'standard',
          isDev: false,
          authenticated: true,
          loginTime: new Date().toISOString()
        };
        localStorage.setItem('v3tax_auth', JSON.stringify(authData));

        trackPlatformActivity("login", {
          source: "metamask",
          walletAddress: authData.walletAddress,
          plan: authData.plan,
          userId: authData.userId || null,
        }).catch(() => {});
        
        try {
          sessionStorage.removeItem("v3tax:force_logged_out");
        } catch {}
        
        return { success: true, data };
      } else {
        const errorData = await response.json();
        throw new Error(errorData.message || 'MetaMask login failed');
      }
    } catch (error) {
      // Handle network errors specifically
      if (error.name === 'TypeError' && error.message.includes('fetch')) {
        const networkError = 'Backend server not reachable. Please check your connection and try again.';
        setError(networkError);
        return { success: false, error: networkError };
      }
      
      setError(error.message);
      return { success: false, error: error.message };
    }
  };

  const logout = async () => {
    try {
            
      // 🚀 WICHTIG: Lokalen Auth-State aus localStorage löschen
      try {
        localStorage.removeItem('v3tax_auth');
              } catch (localError) {
              }
      
      // 🚀 WICHTIG: WalletConnect Pairing NICHT löschen - das bleibt für den nächsten Login!
      // Nur die Session wird beendet, aber das Pairing bleibt erhalten
            
      // Sofort den lokalen State zurücksetzen
      setUser(null);
      setIsAuthenticated(false);
      setError(null);
      setLatestPlan(null); // Setze den zuletzt gekauften Plan auf null beim Logout
      latestPlanLoadedRef.current = false; // Reset ref für nächsten Login
      devBypassInProgressRef.current = false; // Reset devBypass ref
      
            
      // Dann Backend-Logout versuchen
      try {
        await fetch(apiUrl('/api/auth/logout'), {
          method: 'POST',
          credentials: 'include'
        });
              } catch (backendError) {
              }
      
            return { success: true };
    } catch (error) {
      // Auch bei Fehlern den lokalen State ausgeloggt lassen
      setUser(null);
      setIsAuthenticated(false);
      setError(null);
      setLatestPlan(null); // Setze den zuletzt gekauften Plan auf null beim Logout
      latestPlanLoadedRef.current = false; // Reset ref für nächsten Login
      devBypassInProgressRef.current = false; // Reset devBypass ref
      
      // 🚀 WICHTIG: Auch bei Fehlern localStorage löschen (aber Pairing behalten!)
      try {
        localStorage.removeItem('v3tax_auth');
        // 🚀 WICHTIG: Pairing NICHT löschen - das bleibt für den nächsten Login!
              } catch (localError) {
              }
      
            return { success: true }; // Lokaler Logout ist immer erfolgreich
    }
  };

  const checkPlanAccess = (requiredPlan = 'free') => {
    if (!isAuthenticated || !user) return false;
    
    // 🚀 FIX: Case-insensitive + elite Support
    const planHierarchy = { 
      free: 0, 
      standard: 1, 
      pro: 2, 
      elite: 3, 
      ultimate: 4, 
      dev: 99 
    };
    
    const userPlanKey = String(user.plan || 'free').trim().toLowerCase();
    const reqKey = String(requiredPlan || 'free').trim().toLowerCase();
    
    return (planHierarchy[userPlanKey] ?? 0) >= (planHierarchy[reqKey] ?? 0);
  };

  // Stelle sicher, dass der Context-Wert immer verfügbar ist
  const value = {
    user,
    isAuthenticated,
    isLoading,
    error,
    source,
    setSource,
    latestPlan, // 🆕 NEU: Zuletzt gekaufter Plan
    loadLatestPlan, // 🆕 NEU: Funktion zum Laden des Plans
    login,
    devBypass,
    loginWithMetaMask,
    logout,
    checkPlanAccess,
    checkAuthStatus,
    applyLocalAuth // 🔹 Macht lokalen Auth sofort im UI sichtbar
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
